import axios from 'axios';
import { API_URL } from '../config';

const API_BASE = `${API_URL}/api/v1`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      const isAuthEndpoint = url.includes('/auth/login') || url.includes('/customers/login') || url.includes('/customers/register') || url.includes('/forgot-password') || url.includes('/reset-password');
      if (!isAuthEndpoint) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.dispatchEvent(new Event('auth:session-expired'));
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (credentials) => api.post('/auth/login', credentials);
export const register = (userData) => api.post('/auth/register', userData);
export const getCurrentUser = () => api.get('/auth/me');
export const sendVerificationCode = (email) => api.post('/auth/send-verification-code', { email });
export const verifyEmail = (email, code) => api.post('/auth/verify-email', { email, code });
export const forgotPassword = (email) => api.post('/auth/forgot-password', { email });
export const resetPassword = (email, code, new_password) => api.post('/auth/reset-password', { email, code, new_password });
export const changePassword = (newPassword) => api.post('/auth/change-password', { newPassword });

// Businesses
export const getBusinesses = (params) => api.get('/businesses', { params });
export const getBusiness = (id) => api.get(`/businesses/${id}`);
export const createBusiness = (data) => api.post('/businesses', data);
export const updateBusiness = (id, data) => api.put(`/businesses/${id}`, data);
export const deleteBusiness = (id) => api.delete(`/businesses/${id}`);

// Branches
export const getBranches = (businessId) => api.get('/branches', { params: { business_id: businessId } });
export const getBranch = (id) => api.get(`/branches/${id}`);
export const createBranch = (data) => api.post('/branches', data);
export const updateBranch = (id, data) => api.put(`/branches/${id}`, data);
export const deleteBranch = (id) => api.delete(`/branches/${id}`);

// Products
export const getProducts = (params) => api.get('/products', { params });
export const getProduct = (id) => api.get(`/products/${id}`);
export const createProduct = (data) => api.post('/products', data);
export const updateProduct = (id, data) => api.put(`/products/${id}`, data);
export const deleteProduct = (id) => api.delete(`/products/${id}`);
export const downloadProductTemplate = () => api.get('/products/template/csv', { responseType: 'blob' });
export const uploadProductsCSV = (businessId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/products/bulk-upload/${businessId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// POS Terminals
export const getPOSTerminals = (branchId) => api.get(`/pos-terminals/branch/${branchId}`);
export const getPOSTerminal = (id) => api.get(`/pos-terminals/${id}`);
export const createPOSTerminal = (data) => api.post('/pos-terminals', data);
export const updatePOSTerminal = (id, data) => api.put(`/pos-terminals/${id}`, data);

// Cashiers (Cajeros)
export const getCashiers = (businessId) => api.get(`/cashiers/business/${businessId}`);
export const getCashier = (id) => api.get(`/cashiers/${id}`);
export const createCashier = (data) => api.post('/cashiers', data);
export const updateCashier = (id, data) => api.put(`/cashiers/${id}`, data);
export const deleteCashier = (id) => api.delete(`/cashiers/${id}`);
export const forceLogoutTerminal = (terminalId) => api.post(`/cashiers/pos/force-logout?terminal_id=${terminalId}`);

// Business Clients (Clientes para facturación)
export const getClients = (businessId, params = {}) => api.get(`/clients/business/${businessId}`, { params });
export const createClient = (data) => api.post('/clients', data);
export const updateClient = (id, data) => api.put(`/clients/${id}`, data);
export const deleteClient = (id) => api.delete(`/clients/${id}`);

// POS Session — credentials sent via headers, never in URL
export const posLogin = (terminalHash, pin, deviceId) =>
  api.post('/cashiers/pos/login', { terminal_hash: terminalHash, pin, device_id: deviceId });

// Helper: build POS session headers from session object
const _posHeaders = (session) => {
  if (!session) return {};
  const deviceId = localStorage.getItem('pos_device_id') || '';
  return {
    'X-POS-Session-Id': session.session_id,
    'X-POS-Terminal-Id': session.terminal.id,
    'X-POS-Device-Id': deviceId,
  };
};
// Legacy helper for functions that receive individual params
const _posHeadersRaw = (sessionId, terminalId, deviceId) => ({
  'X-POS-Session-Id': sessionId,
  'X-POS-Terminal-Id': terminalId,
  'X-POS-Device-Id': deviceId,
});

export const posLogout = (sessionId, terminalId) =>
  api.post('/cashiers/pos/logout', null, { headers: { 'X-POS-Session-Id': sessionId, 'X-POS-Terminal-Id': terminalId } });
export const validatePosSession = (sessionId, terminalId, deviceId) =>
  api.get('/cashiers/pos/validate-session', { headers: _posHeadersRaw(sessionId, terminalId, deviceId) });

// POS Data (Session-based auth via headers)
export const getPosInventory = (sessionId, terminalId, deviceId, branchId) =>
  api.get(`/cashiers/pos/inventory?branch_id=${branchId}`, { headers: _posHeadersRaw(sessionId, terminalId, deviceId) });
export const getPosSales = (sessionId, terminalId, deviceId, branchId, startDate = null, endDate = null) => {
  let url = `/cashiers/pos/sales?branch_id=${branchId}`;
  if (startDate) url += `&start_date=${startDate}`;
  if (endDate) url += `&end_date=${endDate}`;
  return api.get(url, { headers: _posHeadersRaw(sessionId, terminalId, deviceId) });
};
export const createPosSale = (sessionId, terminalId, deviceId, saleData) =>
  api.post('/cashiers/pos/sales', saleData, { headers: _posHeadersRaw(sessionId, terminalId, deviceId) });

// Online Orders (POS session-authenticated via headers)
export const getOnlineOrders = (branchId, includeCompleted = false, session = null) =>
  api.get(`/orders/branch/${branchId}?include_completed=${includeCompleted}`, { headers: _posHeaders(session) });
export const getOnlineOrdersCount = (branchId, session = null) =>
  api.get(`/orders/branch/${branchId}/new-count`, { headers: _posHeaders(session) });
export const getOrder = (orderId, session = null) =>
  api.get(`/orders/${orderId}`, { headers: _posHeaders(session) });
export const updateOrderStatus = (orderId, status, trackingNumber = null, session = null) => {
  let url = `/orders/${orderId}/status?new_status=${status}`;
  if (trackingNumber) url += `&tracking_number=${trackingNumber}`;
  return api.put(url, null, { headers: _posHeaders(session) });
};
export const markOrderAsRead = (orderId, session = null) =>
  api.put(`/orders/${orderId}/mark-read`, null, { headers: _posHeaders(session) });
export const getOrderHistory = (branchId, limit = 50, session = null) =>
  api.get(`/orders/branch/${branchId}/history?limit=${limit}`, { headers: _posHeaders(session) });
export const createTestOrder = (branchId, businessId) =>
  api.post(`/orders/test/create-sample?branch_id=${branchId}&business_id=${businessId}`);

// Sales
export const getSales = (params) => api.get('/sales', { params });
export const getSale = (id) => api.get(`/sales/${id}`);
export const createSale = (data) => api.post('/sales', data);
export const getUnifiedSales = (params) => api.get('/sales', { params });
export const getDeliveryProof = (orderId) => api.get(`/sales/delivery-proof/${orderId}`);

// Inventory
export const getBranchInventory = (branchId) => api.get(`/inventory/branch/${branchId}`);
export const adjustInventory = (productId, branchId, adjustment) => 
  api.post(`/inventory/adjust?product_id=${productId}&branch_id=${branchId}`, adjustment);
export const transferInventory = (productId, fromBranchId, toBranchId, quantity) =>
  api.post(`/inventory/transfer?product_id=${productId}&from_branch_id=${fromBranchId}&to_branch_id=${toBranchId}&quantity=${quantity}`);
export const downloadInventoryTemplate = () => api.get('/inventory/template', { responseType: 'blob' });
export const uploadInventoryCSV = (branchId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/inventory/bulk-upload/${branchId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const quickAddStock = (branchId, productId, quantity) =>
  api.post(`/inventory/quick-add/${branchId}?product_id=${productId}&quantity=${quantity}`);
export const toggleInventoryMarketplaceVisibility = (productId, branchId, hidden) =>
  api.patch(`/inventory/${productId}/${branchId}/marketplace-visibility`, { hidden_from_marketplace: hidden });

// Reports
export const getDashboardStats = (businessId) => api.get('/reports/dashboard-stats', { params: { business_id: businessId } });
export const getDailySales = (params) => api.get('/reports/daily-sales', { params });
export const getBranchPerformance = (params) => api.get('/reports/branch-performance', { params });
export const getLowStock = (businessId) => api.get('/reports/low-stock', { params: { business_id: businessId } });
export const exportLowStockExcel = (businessId) => api.get('/reports/low-stock/export', { 
  params: { business_id: businessId },
  responseType: 'blob'
});
export const getCashClose = (params) => api.get('/reports/cash-close', { params });
export const getSalesByPeriod = (params) => api.get('/reports/sales-by-period', { params });
export const getTopProducts = (params) => api.get('/reports/top-products', { params });
export const getProfitMargin = (params) => api.get('/reports/profit-margin', { params });
export const getMarketplaceOrdersReport = (params) => api.get('/reports/marketplace-orders', { params });
export const getQuotesFunnel = (params) => api.get('/reports/quotes-funnel', { params });
export const getInventoryValuation = (params) => api.get('/reports/inventory-valuation', { params });
export const getSalesDrill = (params) => api.get('/reports/sales-drill', { params });
export const recalculateSalesDrill = (params) => api.post('/reports/sales-drill/recalculate', null, { params });

// Categories
export const getCategories = (activeOnly = true) => api.get('/categories', { params: { active_only: activeOnly } });
export const getCategoriesPublic = () => api.get('/categories/public');
export const createCategory = (data) => api.post('/categories', data);
export const updateCategory = (id, data) => api.put(`/categories/${id}`, data);
export const deleteCategory = (id) => api.delete(`/categories/${id}`);
export const seedCategories = () => api.post('/categories/seed');
export const resetAndSeedCategories = () => api.post('/categories/reset-and-seed');
export const autoCategorizeProducts = () => api.post('/categories/auto-categorize');
export const uploadCategoryImage = (categoryId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/categories/upload-image/${categoryId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Sections (per-business)
export const getSections = (params) => api.get('/sections', { params });
export const createSection = (data) => api.post('/sections', data);
export const updateSection = (id, data) => api.put(`/sections/${id}`, data);
export const deleteSection = (id) => api.delete(`/sections/${id}`);

// Master Categories (top-level hierarchy)
export const getMasterCategories = (params) => api.get('/master-categories', { params });
export const getMasterCategoriesPublic = () => api.get('/master-categories/public');
export const createMasterCategory = (data) => api.post('/master-categories', data);
export const updateMasterCategory = (id, data) => api.put(`/master-categories/${id}`, data);
export const deleteMasterCategory = (id) => api.delete(`/master-categories/${id}`);
export const seedMasterCategories = () => api.post('/master-categories/seed');
export const migrateMasterCategories = () => api.post('/master-categories/migrate-products');
export const getMarketplaceMasterCategories = () => api.get('/marketplace/master-categories');
export const uploadMasterCategoryImage = (categoryId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/master-categories/upload-image/${categoryId}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Business Types Catalog
export const getBusinessTypesPublic = () => api.get('/business-types/public');
export const getBusinessTypes = (params) => api.get('/business-types', { params });
export const createBusinessType = (data) => api.post('/business-types', data);
export const updateBusinessType = (id, data) => api.put(`/business-types/${id}`, data);
export const deleteBusinessType = (id) => api.delete(`/business-types/${id}`);
export const seedBusinessTypes = () => api.post('/business-types/seed');
export const getMarketplaceBusinessTypes = () => api.get('/marketplace/business-types');

// Support Tickets
export const createTicket = (data) => api.post('/tickets', data);
export const getTickets = (params) => api.get('/tickets', { params });
export const getTicketStats = () => api.get('/tickets/stats');
export const getTicket = (id) => api.get(`/tickets/${id}`);
export const updateTicketStatus = (id, status) => api.put(`/tickets/${id}/status?new_status=${status}`);
export const addTicketMessage = (id, content) => api.post(`/tickets/${id}/messages?content=${encodeURIComponent(content)}`);

// Uploads
export const uploadProductImage = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/uploads/product-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const uploadLogo = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/uploads/logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Facturama Certificate Uploads
export const uploadFacturamaCertificate = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/uploads/facturama-certificate', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const uploadFacturamaPrivateKey = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/uploads/facturama-private-key', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// ==================== Facturación API ====================

// CSD Management
export const registrarCSD = (data) => api.post('/facturacion/csd/registrar', data);
export const getCSDStatus = () => api.get('/facturacion/csd/estatus');
export const diagnosticarCSD = () => api.get('/facturacion/csd/diagnostico');

// Invoices
export const crearFactura = (data) => api.post('/facturacion/facturas/crear', data);
export const facturarTicket = (data) => api.post('/facturacion/facturar-ticket', data);
export const getFacturas = (params) => api.get('/facturacion/facturas', { params });
export const getFactura = (id) => api.get(`/facturacion/facturas/${id}`);
export const cancelarFactura = (id, data) => api.post(`/facturacion/facturas/${id}/cancelar`, data);
export const descargarFacturaPDF = (id) => api.get(`/facturacion/facturas/${id}/pdf`, { responseType: 'blob' });
export const descargarFacturaXML = (id) => api.get(`/facturacion/facturas/${id}/xml`, { responseType: 'blob' });
export const enviarFacturaEmail = (id, data) => api.post(`/facturacion/facturas/${id}/enviar-email`, data);

// Tickets for invoicing
export const getTicketsPendientesFacturar = (params) => api.get('/sales/pendientes-facturar', { params });
export const buscarTicketPendiente = (q) => api.get('/sales/pendientes-facturar', { params: { q } });

// SAT Catalogs
export const getRegimenesFiscales = () => api.get('/facturacion/catalogos/regimenes-fiscales');
export const getUsosCFDI = () => api.get('/facturacion/catalogos/usos-cfdi');
export const getFormasPago = () => api.get('/facturacion/catalogos/formas-pago');
export const getMetodosPago = () => api.get('/facturacion/catalogos/metodos-pago');
export const buscarProductosSAT = (q) => api.get('/facturacion/catalogos/productos', { params: { q } });
export const buscarUnidadesSAT = (q) => api.get('/facturacion/catalogos/unidades', { params: { q } });

// Autofacturación (public - no auth required)
export const getTicketParaAutofactura = (ticketId) => api.get(`/autofactura/ticket/${ticketId}`);
export const autofacturarTicket = (ticketId, data) => api.post(`/autofactura/ticket/${ticketId}/facturar`, data);
export const descargarPDFAutofactura = (facturaId) => api.get(`/autofactura/factura/${facturaId}/pdf`, { responseType: 'blob' });
export const descargarXMLAutofactura = (facturaId) => api.get(`/autofactura/factura/${facturaId}/xml`, { responseType: 'blob' });
export const reenviarEmailAutofactura = (facturaId, data) => api.post(`/autofactura/factura/${facturaId}/reenviar-email`, data);

// Public SAT Catalogs for Autofacturación (no auth required)
export const getRegimenesFiscalesPublic = () => api.get('/autofactura/catalogos/regimenes-fiscales');
export const getUsosCFDIPublic = () => api.get('/autofactura/catalogos/usos-cfdi');
export const getFormasPagoPublic = () => api.get('/autofactura/catalogos/formas-pago');

// Cotizaciones
export const getQuotes = (params) => api.get('/quotes', { params });
export const getQuote = (id) => api.get(`/quotes/${id}`);
export const createQuote = (data) => api.post('/quotes', data);
export const updateQuote = (id, data) => api.put(`/quotes/${id}`, data);
export const deleteQuote = (id) => api.delete(`/quotes/${id}`);
export const sendQuote = (id, data) => api.post(`/quotes/${id}/send`, data);
export const acceptQuote = (id) => api.post(`/quotes/${id}/accept`);
export const rejectQuote = (id) => api.post(`/quotes/${id}/reject`);
export const duplicateQuote = (id) => api.post(`/quotes/${id}/duplicate`);
export const convertQuoteToSale = (id, data) => api.post(`/quotes/${id}/convert-to-sale`, data);
export const downloadQuotePDF = (id) => api.get(`/quotes/${id}/pdf`, { responseType: 'blob' });
export const getPublicQuote = (token) => api.get(`/quotes/public/${token}`);
export const downloadPublicQuotePDF = (token) => api.get(`/quotes/public/${token}/pdf`, { responseType: 'blob' });

// ==================== Licenses API ====================

// License Plans
export const getLicensePlans = () => api.get('/licenses/plans');
export const createLicensePlan = (data) => api.post('/licenses/plans', data);

// Licenses
export const getLicenses = (params) => api.get('/licenses', { params });
export const getLicense = (id) => api.get(`/licenses/${id}`);
export const generateLicense = (data) => api.post('/licenses/generate', data);
export const linkLicense = (id, data) => api.put(`/licenses/${id}/link`, data);
export const revokeLicense = (id, reason) => api.put(`/licenses/${id}/revoke`, null, { params: { reason } });

// POS Downloads
export const getPosDownloads = (params) => api.get('/pos-downloads', { params });
export const getLatestPosDownload = (posType) => api.get(`/pos-downloads/latest/${posType}`);
export const createPosDownload = (data) => api.post('/pos-downloads', data);
export const deactivatePosDownload = (id) => api.put(`/pos-downloads/${id}/deactivate`);

// System Config - Facturama
export const getFacturamaConfig = () => api.get('/system-config/facturama');
export const updateFacturamaConfig = (data) => api.put('/system-config/facturama', data);

// System Config - Placeholder Images
export const getPlaceholderConfig = () => api.get('/system-config/placeholder-images');
export const updatePlaceholderConfig = (data) => api.put('/system-config/placeholder-images', data);

// System Config - Landing Layout
export const getLandingLayout = () => api.get('/system-config/landing-layout');
export const updateLandingLayout = (data) => api.put('/system-config/landing-layout', data);

// System Config - Social Links
export const getSocialLinks = () => api.get('/system-config/social-links');
export const updateSocialLinks = (data) => api.put('/system-config/social-links', data);

// Marketplace - Customer Verification
export const sendCustomerVerificationCode = (email) => api.post('/marketplace/customers/send-verification-code', { email });
export const verifyCustomerEmail = (email, code) => api.post('/marketplace/customers/verify-email', { email, code });
export const customerForgotPassword = (email) => api.post('/marketplace/customers/forgot-password', { email });
export const customerResetPassword = (email, code, new_password) => api.post('/marketplace/customers/reset-password', { email, code, new_password });

// Marketplace - Featured / New / Offers / Search Stores
export const getFeaturedStores = () => api.get('/marketplace/featured-stores');
export const getNewStores = () => api.get('/marketplace/new-stores');
export const getOffers = () => api.get('/marketplace/offers');
export const searchStores = (params) => api.get('/marketplace/stores/search', { params });

// ==================== License Commerce API ====================

// Software Products
export const getCatalogProducts = (params) => api.get('/license-commerce/products', { params });
export const createCatalogProduct = (data) => api.post('/license-commerce/products', data);
export const updateCatalogProduct = (id, data) => api.put(`/license-commerce/products/${id}`, data);

// Catalog Packages
export const getCatalogPackages = (params) => api.get('/license-commerce/packages', { params });
export const getCatalogPackage = (id) => api.get(`/license-commerce/packages/${id}`);
export const createCatalogPackage = (data) => api.post('/license-commerce/packages', data);
export const updateCatalogPackage = (id, data) => api.put(`/license-commerce/packages/${id}`, data);

// Discount Rules
export const getDiscountRules = () => api.get('/license-commerce/discounts');
export const createDiscountRule = (data) => api.post('/license-commerce/discounts', data);
export const updateDiscountRule = (id, data) => api.put(`/license-commerce/discounts/${id}`, data);
export const deleteDiscountRule = (id) => api.delete(`/license-commerce/discounts/${id}`);
export const seedDiscountRules = () => api.post('/license-commerce/discounts/seed');

// Price Calculation
export const calculateLicensePrice = (data) => api.post('/license-commerce/calculate-price', data);
export const calculateUpgradePrice = (data) => api.post('/license-commerce/calculate-upgrade', data);

// License Orders
export const getLicenseOrders = (params) => api.get('/license-commerce/orders', { params });
export const getLicenseOrder = (id) => api.get(`/license-commerce/orders/${id}`);
export const createLicenseOrder = (data) => api.post('/license-commerce/orders', data);
export const createLicenseUpgrade = (data) => api.post('/license-commerce/orders/upgrade', data);
export const cancelLicenseOrder = (id) => api.put(`/license-commerce/orders/${id}/cancel`);

// ==================== Delivery Tracking (Business Dashboard) ====================
export const getActiveDeliveryRoutes = (businessId) => api.get(`/delivery/routes/business/${businessId}/active`);
export const getRouteGeometryAdmin = (routeId, driverLocation) => api.post(`/delivery/routes/${routeId}/geometry/admin`, { driver_location: driverLocation });

// ==================== Digital Menu API ====================

// Digital Menu - Business owner
export const configureDigitalMenu = (data) => api.post('/digital-menu/configure', data);
export const getMyDigitalMenu = () => api.get('/digital-menu/my-menu');
export const checkMenuSlug = (slug) => api.get('/digital-menu/check-slug', { params: { slug } });
export const uploadMenuBanner = (formData) => api.post('/digital-menu/upload-banner', formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
export const getMenuQRCode = () => api.get('/digital-menu/qr-code', { responseType: 'blob' });
export const getMenuStats = () => api.get('/digital-menu/stats');
export const getMenuLicenseStatus = () => api.get('/digital-menu/license-status');
export const activateMenuLicense = () => api.post('/digital-menu/activate-license');

// Digital Menu - Public (no auth)
export const getPublicMenu = (slug) => api.get(`/digital-menu/public/${slug}`);
export const getPublicMenuProducts = (slug, params) => api.get(`/digital-menu/public/${slug}/products`, { params });
export const getPublicMenuProduct = (slug, productId) => api.get(`/digital-menu/public/${slug}/product/${productId}`);
export const validateMenuDelivery = (slug, data) => api.post(`/digital-menu/public/${slug}/validate-delivery`, data);
export const createMenuOrder = (slug, customerToken, data) =>
  api.post(`/digital-menu/public/${slug}/order?customer_token=${customerToken}`, data);
export const getMyMenuOrders = (slug, customerToken) =>
  api.get(`/digital-menu/public/${slug}/my-orders?customer_token=${customerToken}`);

// ==================== Admin Purchases API ====================

export const getPurchaseDashboard = (params) => api.get('/admin/purchases/dashboard', { params });
export const getCheckoutSessions = (params) => api.get('/admin/purchases/checkout-sessions', { params });
export const getCheckoutSessionDetail = (sessionId) => api.get(`/admin/purchases/checkout-sessions/${sessionId}`);
export const getCompletedPurchases = (params) => api.get('/admin/purchases/completed', { params });
export const getPurchasedLicenses = (params) => api.get('/admin/purchases/licenses', { params });
export const getSoldAddons = (params) => api.get('/admin/purchases/addons', { params });
export const getTimbresSales = (params) => api.get('/admin/purchases/timbres', { params });
export const getWebPurchaseUsers = (params) => api.get('/admin/purchases/users', { params });
export const getAbandonedCarts = (params) => api.get('/admin/purchases/abandoned-carts', { params });

// ==================== V2 License Management API ====================

const apiv2 = axios.create({
  baseURL: `${API_URL}/api/v2`,
  headers: { 'Content-Type': 'application/json' },
});

apiv2.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

apiv2.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.dispatchEvent(new Event('auth:session-expired'));
    }
    return Promise.reject(error);
  }
);

// Addon Catalog
export const getAddonCatalog = () => apiv2.get('/license-management/admin/addon-catalog');
export const createAddonCatalog = (data) => apiv2.post('/license-management/admin/addon-catalog', data);
export const updateAddonCatalog = (code, data) => apiv2.put(`/license-management/admin/addon-catalog/${code}`, data);

// License Addons
export const getLicenseAddons = (serial) => apiv2.get(`/license-management/admin/licenses/${serial}/addons`);
export const assignAddon = (serial, data) => apiv2.post(`/license-management/admin/licenses/${serial}/addons`, data);
export const cancelAddon = (serial, code) => apiv2.delete(`/license-management/admin/licenses/${serial}/addons/${code}`);

// Stamps
export const rechargeStamps = (data) => apiv2.post('/license-management/admin/stamps/recharge', data);
export const adjustStamps = (data) => apiv2.post('/license-management/admin/stamps/adjust', data);
export const getStampTransactions = (serial, params) => apiv2.get(`/license-management/admin/stamps/${serial}/transactions`, { params });

// Stamp Balance + Packages
export const getStampBalance = (serial) => apiv2.get(`/license-management/admin/stamps/${serial}/balance`);
export const getStampPackages = () => apiv2.get('/license-management/admin/stamp-packages');
export const createStampPackage = (data) => apiv2.post('/license-management/admin/stamp-packages', data);
export const updateStampPackage = (id, data) => apiv2.put(`/license-management/admin/stamp-packages/${id}`, data);

// Admin Invoices
export const getAdminInvoices = (params) => apiv2.get('/invoicing/admin/invoices', { params });
export const getAdminInvoicesBySerial = (serial, params) => apiv2.get(`/invoicing/admin/invoices/by-serial/${serial}`, { params });

// License Management Dashboard
export const getLicenseMgmtDashboard = () => apiv2.get('/license-management/admin/dashboard');

// ==================== Admin Sync V2 API ====================

export const getSyncPriceLists = (params) => apiv2.get('/admin/sync/price-lists', { params });
export const updateSyncPriceList = (id, data) => apiv2.put(`/admin/sync/price-lists/${id}`, data);
export const deleteSyncPriceList = (id) => apiv2.delete(`/admin/sync/price-lists/${id}`);
export const getSyncShiftSummaries = (params) => apiv2.get('/admin/sync/shift-summaries', { params });
export const getSyncLog = (params) => apiv2.get('/admin/sync/sync-log', { params });
export const getSyncInventoryAdjustments = (params) => apiv2.get('/admin/sync/inventory-adjustments', { params });

// ==================== Business Sync V2 API (owner views) ====================

export const getMyPriceLists = (params) => apiv2.get('/sync/my/price-lists', { params });
export const updateMyPriceList = (id, data) => apiv2.put(`/sync/my/price-lists/${id}`, data);
export const deleteMyPriceList = (id) => apiv2.delete(`/sync/my/price-lists/${id}`);
export const getMyShiftSummaries = (params) => apiv2.get('/sync/my/shift-summaries', { params });
export const getMySyncLog = (params) => apiv2.get('/sync/my/sync-log', { params });
export const getMyInventoryAdjustments = (params) => apiv2.get('/sync/my/inventory-adjustments', { params });

// ==================== Delivery Hub ====================

export const getDhOrders = (params) => api.get('/delivery-hub/orders', { params });
export const getDhOrder = (id) => api.get(`/delivery-hub/orders/${id}`);
export const updateDhOrderStatus = (id, data) => api.patch(`/delivery-hub/orders/${id}/status`, data);
export const getDhStats = () => api.get('/delivery-hub/stats');
export const getDhUsage = (params) => api.get('/delivery-hub/usage', { params });
export const getDhActive = () => api.get('/delivery-hub/active');
export const getDhChannels = () => api.get('/delivery-hub/channels');
export const createDhChannel = (data) => api.post('/delivery-hub/channels', data);
export const getDhMenuMappings = (params) => api.get('/delivery-hub/menu-mappings', { params });
export const createDhMenuMapping = (data) => api.post('/delivery-hub/menu-mappings', data);
export const deleteDhMenuMapping = (id) => api.delete(`/delivery-hub/menu-mappings/${id}`);
export const sendMockDhOrder = (data) => api.post('/delivery-hub/webhooks/mock', data);

// ==================== WhatsApp Hub ====================

export const getWaAccounts = () => api.get('/whatsapp/accounts');
export const createWaAccount = (data) => api.post('/whatsapp/accounts', data);
export const updateWaAccount = (id, data) => api.put(`/whatsapp/accounts/${id}`, data);
export const deleteWaAccount = (id) => api.delete(`/whatsapp/accounts/${id}`);
export const getWaConversations = (params) => api.get('/whatsapp/conversations', { params });
export const getWaMessages = (conversationId, params) => api.get(`/whatsapp/conversations/${conversationId}/messages`, { params });
export const sendWaMessage = (data) => api.post('/whatsapp/conversations/send', data);
export const markWaConversationRead = (id) => api.put(`/whatsapp/conversations/${id}/read`);
export const closeWaConversation = (id) => api.put(`/whatsapp/conversations/${id}/close`);

// WhatsApp Auto-Replies
export const getWaAutoReplies = () => api.get('/whatsapp/auto-replies');
export const createWaAutoReply = (data) => api.post('/whatsapp/auto-replies', data);
export const updateWaAutoReply = (id, data) => api.put(`/whatsapp/auto-replies/${id}`, data);
export const deleteWaAutoReply = (id) => api.delete(`/whatsapp/auto-replies/${id}`);
export const toggleWaAutoReply = (id) => api.put(`/whatsapp/auto-replies/${id}/toggle`);
export const testWaAutoReply = (data) => api.post('/whatsapp/auto-replies/test', data);

// WhatsApp Templates
export const getWaTemplates = (params) => api.get('/whatsapp/templates', { params });
export const createWaTemplate = (data) => api.post('/whatsapp/templates', data);
export const syncWaTemplates = (accountId) => api.post(`/whatsapp/templates/sync/${accountId}`);
export const updateWaTemplate = (id, data) => api.put(`/whatsapp/templates/${id}`, data);
export const deleteWaTemplate = (id) => api.delete(`/whatsapp/templates/${id}`);
export const sendWaTemplate = (id, data) => api.post(`/whatsapp/templates/${id}/send`, data);

// WhatsApp Quick Replies
export const getWaQuickReplies = () => api.get('/whatsapp/quick-replies');
export const createWaQuickReply = (data) => api.post('/whatsapp/quick-replies', data);
export const updateWaQuickReply = (id, data) => api.put(`/whatsapp/quick-replies/${id}`, data);
export const deleteWaQuickReply = (id) => api.delete(`/whatsapp/quick-replies/${id}`);

// WhatsApp Notifications
export const getWaNotificationLog = (params) => api.get('/whatsapp/notifications/log', { params });
export const getWaNotificationStats = () => api.get('/whatsapp/notifications/stats');

// WhatsApp Contacts
export const getWaContacts = (params) => api.get('/whatsapp/contacts', { params });
export const getWaContact = (phone) => api.get(`/whatsapp/contacts/by-phone/${encodeURIComponent(phone)}`);
export const getWaContactById = (id) => api.get(`/whatsapp/contacts/${id}`);
export const updateWaContact = (id, data) => api.put(`/whatsapp/contacts/${id}`, data);
export const deleteWaContact = (id) => api.delete(`/whatsapp/contacts/${id}`);
export const addWaContactTags = (id, data) => api.post(`/whatsapp/contacts/${id}/tags`, data);

// WhatsApp Event Settings
export const getWaEventSettings = () => api.get('/whatsapp/event-settings');
export const updateWaEventSetting = (eventKey, data) => api.put(`/whatsapp/event-settings/${eventKey}`, data);
export const resetWaEventSetting = (eventKey) => api.post(`/whatsapp/event-settings/${eventKey}/reset`);

// WhatsApp Media
export const uploadWaMedia = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/whatsapp/media/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
export const getWaMediaUrl = (mediaId, accountId) =>
  `${API_BASE}/whatsapp/media/download/${mediaId}?account_id=${encodeURIComponent(accountId)}`;

// WhatsApp Agents (multi-agent)
export const getWaAgents = () => api.get('/whatsapp/conversations/agents');
export const assignWaConversation = (id, data) => api.put(`/whatsapp/conversations/${id}/assign`, data);
export const unassignWaConversation = (id) => api.put(`/whatsapp/conversations/${id}/unassign`);

// Superadmin User Management
export const getSuperadminUsers = () => api.get('/superadmin-users');
export const getPermissionsCatalog = () => api.get('/superadmin-users/permissions/catalog');
export const createSuperadminUser = (data) => api.post('/superadmin-users', data);
export const updateSuperadminUser = (id, data) => api.put(`/superadmin-users/${id}`, data);
export const resetSuperadminPassword = (id, password) => api.put(`/superadmin-users/${id}/reset-password`, { password });
export const deactivateSuperadminUser = (id) => api.delete(`/superadmin-users/${id}`);

export default api;